import * as HLF from "html-living-framework";
import { Application } from "../Application";
import * as Constants from "../Data/Constants";
import * as TestJSON from "../Data/json/testSong.json";
import { SongData } from "../Data/SongData";
import * as Topics from "../Data/Topics";
import Gameplay from "../Gameplay";
import { Game } from "../States/Game";
import { Button } from "./Button";
import { Character, CharacterName } from "./Character";

// This doesn't need to be a Container but it's helpful for lifecycle stuff
export default class MusicManager extends PIXI.Container {

    /** Current time of the backing track */
    public get DEBUG_currentTime(): string {
        return this._baseTrackTEMP!.getTimePos().toFixed(4).toString();
    }
    /** The final solo start time, before the track loops */
    public get DEBUG_lastAvailableTime(): string {
        return this._lastAvailableTimeStampValue.toString();
    }
    /** The next upcoming solo start time */
    public get DEBUG_nextAvailableTime(): string {
        return this._bgSongData.TimeStamps[this._nextTimeStamp].toString();
    }
    /** Maximum difference in seconds for how out of sync the soloist and the backing track are allowed to be */
    protected static readonly MIN_PLAY_TIME_DIFF: number = 0.01;
    /**
     * Minimum duration, in seconds, between solo request time and assigned start time.
     *
     * i.e. if there's not enough time to prepare for your solo, wait until the following start point
     */
    protected static readonly MIN_NEXT_TIME_DIFF: number = 0.1;
    protected _baseTrackTEMP: HLF.IAudioTrack | undefined;
    protected _topTrackTEMP: HLF.IAudioTrack | undefined;
    protected _nextTimeStamp: number = 0;
    protected _bgSongData: SongData;
    protected _lastAvailableTimeStampValue: number;
    protected _barTime: number;
    protected _subscriptions: any[];
    protected _previousSongTime: number = 0;
    protected _newSongTime: number = 0;
    protected _nextTimestampSolos: CharacterName[];
    protected _currentTimestampSolos: CharacterName[];
    protected _timeToPlaySolo: number = 0;

    constructor() {
        super();
        this._topTrackTEMP = Application.instance.audio.getAudioTrack(
                                Constants.MUSIC_LAYER_TOP,
                                HLF.AudioCategory.BGM,
                            );
        this._baseTrackTEMP = Application.instance.audio.getAudioTrack(
                                Constants.MUSIC_LAYER_BASE,
                                HLF.AudioCategory.BGM,
                            );

        this._baseTrackTEMP!.on(HLF.HowlerUtils.Events.END, this.onBaseLoop.bind(this));

        this._bgSongData = new SongData(TestJSON.bg.timestamps);
        this._lastAvailableTimeStampValue = this._bgSongData.TimeStamps[this._bgSongData.TimeStamps.length - 2];
        this._barTime = this._bgSongData.TimeStamps[0];
        this._nextTimestampSolos = new Array<CharacterName>();
        this._currentTimestampSolos = new Array<CharacterName>();

        this._subscriptions = new Array<any>();
        this._subscriptions.push(
            PubSub.subscribe(Topics.SOLO_REQUESTED, this.onSoloRequested.bind(this)),
            PubSub.subscribe(Topics.CANCEL_SOLO, this.onSoloCancelled.bind(this)),
            PubSub.subscribe(Topics.SOLO_ENDED, this.onSoloEnd.bind(this)),
            PubSub.subscribe(Topics.CULTURE_BUTTON_PRESSED, this.onCultureButtonPressed.bind(this)),
            PubSub.subscribe(Topics.GAME_PAUSED, this.onPause.bind(this)),
            PubSub.subscribe(Topics.GAME_UNPAUSED, this.onResume.bind(this)),
            PubSub.subscribe(Topics.START_INTRO, this.startIntro.bind(this)),
        );
    }

    public destroy(pOptions?: PIXI.DestroyOptions | boolean): void {
        super.destroy(pOptions);
        this._topTrackTEMP?.stop();
        this._baseTrackTEMP?.stop();
        this._subscriptions.forEach(PubSub.unsubscribe);
    }

    public update(pDeltaTime: number) {
        if (this._baseTrackTEMP !== undefined) {
            this._previousSongTime = this._newSongTime;
            this._newSongTime = this._baseTrackTEMP.getTimePos();
            const curTimeStamp = this._bgSongData.TimeStamps[this._nextTimeStamp];
            // console.log("Time: %s vs NTS: %s", this._newSongTime, curTimeStamp);
            if (this._newSongTime < this._previousSongTime) {
                // console.log("%cCHANGE from %s!!!", "color: green; font-weight: bold", this._nextTimeStamp);
                // this._baseTrackTEMP.setTimePos(0);
                this._nextTimeStamp = 0;
                this.shiftCurSoloists();
            }
            else {
                if (this._newSongTime >= curTimeStamp && this._nextTimeStamp < 7) {
                    // console.log("%cCHANGE from %s!!!", "color: red; font-weight: bold", this._nextTimeStamp);
                    this._nextTimeStamp = (++this._nextTimeStamp) % this._bgSongData.TimeStamps.length;
                    // this._baseTrackTEMP.setTimePos(curTimeStamp);
                    // this._topTrackTEMP!.setTimePos(curTimeStamp);
                    this.shiftCurSoloists();
                }
            }
        }
    }

    protected startIntro() {
        Application.instance.audio.play(Constants.MUSIC_LAYER_TOP, 1, true, HLF.AudioCategory.BGM);
        Application.instance.audio.play(Constants.MUSIC_LAYER_BASE, 1, true, HLF.AudioCategory.BGM);
    }

    protected onSoloEnd(pTopic: string, pCharacter: CharacterName): void {
        Application.instance.audio.fadeTo(Constants.MUSIC_LAYER_TOP, HLF.AudioCategory.BGM, 1, 500);
    }

    protected onSoloRequested(pTopic: string, pCharacter: CharacterName): void {
        /**
         * If there is time for the next solo, add to currentTimestamps
         * If there isn't, add to nextTimestamps
         */
        // Determine when to play
        const nextAvailableTime: number = this._bgSongData.TimeStamps[this._nextTimeStamp];
        const trackTimePos: number = this._baseTrackTEMP!.getTimePos();
        const timeToNextTimeStamp: number = nextAvailableTime - trackTimePos;
        let waitTime: number = 0;

        // console.log("nextAvailableTime: %c%s", "color: blue", nextAvailableTime);
        // console.log("trackTimePos: %c%s", "color: blue", trackTimePos);
        // console.log("timeToNextTimeStamp: %c%s", "color: blue", timeToNextTimeStamp);
        // console.log("barTime: %c%s", "color: blue", this._barTime);

        // After last time stamp value
        if (trackTimePos > this._lastAvailableTimeStampValue) {
            // console.log("AFTER LAST AVAILABLE TIME STAMP");
            // Less than min delay time
            if (timeToNextTimeStamp < MusicManager.MIN_NEXT_TIME_DIFF) {
                // console.log("LESS THAN MIN AFTER LAST AVAILABLE TIME STAMP");
                waitTime = timeToNextTimeStamp + this._barTime;
                this._nextTimestampSolos.push(pCharacter);
                Gameplay.Instance.characterManager!.characters[pCharacter].setProgressBarValues(0, waitTime);
            }
            // Greater than min delay time
            else {
                // console.log("GREATER THAN MIN AFTER LAST AVAILABLE TIME STAMP");
                waitTime = timeToNextTimeStamp;
                this._currentTimestampSolos.push(pCharacter);
                Gameplay.Instance.characterManager!.characters[pCharacter].setProgressBarValues(0, waitTime);
            }
        }
        // Before last time stamp value
        else {
            const character = Gameplay.Instance.characterManager!.characters[pCharacter]!;
            // console.log("BEFORE LAST AVAILABLE TIME STAMP");
            // Less than min delay time
            if (timeToNextTimeStamp < MusicManager.MIN_NEXT_TIME_DIFF) {
                // console.log("LESS THAN MIN BEFORE LAST AVAILABLE TIME STAMP");
                waitTime = timeToNextTimeStamp + this._barTime;
                this._nextTimestampSolos.push(pCharacter);
                character.setProgressBarValues(trackTimePos, trackTimePos + waitTime);
            }
            // Greater than min delay time
            else {
                // console.log("GREATER THAN MIN BEFORE LAST AVAILABLE TIME STAMP");
                waitTime = timeToNextTimeStamp;
                this._currentTimestampSolos.push(pCharacter);
                character.setProgressBarValues(trackTimePos, nextAvailableTime);
            }
        }
        // console.log("WaitTime: %c%s", "color: blue", waitTime);
    }
    protected startSolo(pCharacter: CharacterName): void {
        // console.log(Character.convertNameToString(pCharacter) + " starting solo");
        const character = Gameplay.Instance.characterManager!.characters[pCharacter]!;

        if (!character.isSoloLoaded) {
            console.warn("Solo not loaded yet!");
            this._currentTimestampSolos.push(pCharacter);
            character.setProgressBarValues(0, this._barTime);
            return;
        }

        const trackCurTime: number = character.getTrackTimePos();
        if (trackCurTime >= 0) {
            if (Math.abs(trackCurTime - this._timeToPlaySolo) < MusicManager.MIN_PLAY_TIME_DIFF) {
                if (this._timeToPlaySolo < trackCurTime) {
                    // console.log("HANDLE SOLO PLAY TIME DIFF GREATER THAN MIN");
                }
                else if (this._timeToPlaySolo > trackCurTime) {
                    character.setTrackTimePos(this._timeToPlaySolo - trackCurTime);
                }
            }

            character.startSolo(this._baseTrackTEMP!.getTimePos());
            Application.instance.audio.fadeTo(Constants.MUSIC_LAYER_TOP, HLF.AudioCategory.BGM, 0, 500);
        }
        else {
            console.warn("Track wasn't found. Possibly because it was unloaded?");
            this._currentTimestampSolos.push(pCharacter);
            // TODO: update character's progress bar
        }
    }
    protected shiftCurSoloists(): void {

        for (let i = this._currentTimestampSolos.length - 1; i >= 0; --i) {
            const soloist = this._currentTimestampSolos.shift() as CharacterName;
            // console.log("Current solo %s", CharacterName[soloist]);
            this.startSolo(soloist);
        }
        for (let i = this._nextTimestampSolos.length - 1; i >= 0; --i) {
            const soloist = this._nextTimestampSolos.shift() as CharacterName;
            // console.log("Shifting solo %s", CharacterName[soloist]);
            this._currentTimestampSolos.push(soloist);
        }
    }

    protected onSoloCancelled(): void {
        // clear any current soloists and their timelines
        for (let i = this._currentTimestampSolos.length - 1; i >= 0; --i) {
            this._currentTimestampSolos.shift();
        }
        for (let i = this._nextTimestampSolos.length - 1; i >= 0; --i) {
            this._nextTimestampSolos.shift();
        }
    }

    protected onBaseLoop(): void {
        // console.error("OnBaseLoop");
        this._topTrackTEMP!.setTimePos(0);
        this._baseTrackTEMP!.setTimePos(0);
        this._nextTimeStamp = 0;
    }

    protected onCultureButtonPressed(pTopic: string, pData: Button): void {
        Application.instance.audio.fadeTo(Constants.MUSIC_LAYER_TOP, HLF.AudioCategory.BGM, 1, 500);
    }

    protected onPause(pTopic: string): void {
       this._topTrackTEMP!.pause();
       this._baseTrackTEMP!.pause();
    }

    protected onResume(pTopic: string): void {
        if (this._topTrackTEMP!.getTimePos() > 0) {
            this._topTrackTEMP!.play();
        }
        if (this._baseTrackTEMP!.getTimePos() > 0) {
            this._baseTrackTEMP!.play();
        }
        this.update(0);
        // this.shiftCurSoloists();
    }
}
